<template>
  <div v-if="visible" class="share-modal-overlay" @click="closeModal">
    <div class="share-modal-content" @click.stop>
      <button class="share-modal-close" @click="closeModal">
        <span>&times;</span>
      </button>
      <div class="share-modal-header">
        <h3 class="share-modal-title">Share Your Report</h3>
      </div>
      
      <div class="share-modal-body">
        <!-- Grid URL -->
        <div class="share-url-section">
          <label class="share-label">Grid URL</label>
          <div class="share-input-wrapper">
            <input 
              type="text" 
              :value="gridUrl" 
              readonly 
              class="share-input"
              ref="gridUrlInput"
            />
            <button @click="copyToClipboard('grid', $event)" class="copy-btn-inline">
              <i class="far fa-copy"></i>
            </button>
          </div>
        </div>

        <!-- Report URL -->
        <div class="share-url-section">
          <label class="share-label">Report URL</label>
          <div class="share-input-wrapper">
            <input 
              type="text" 
              :value="reportUrl" 
              readonly 
              class="share-input"
              ref="reportUrlInput"
            />
            <button @click="copyToClipboard('report', $event)" class="copy-btn-inline">
              <i class="far fa-copy"></i>
            </button>
          </div>
        </div>

        <!-- Embed Code -->
        <div class="share-url-section">
          <label class="share-label">Embed URL</label>
          <div class="share-embed-wrapper">
            <textarea class="embed-code-preview" ref="embedCodeTextarea" readonly>{{ embedCode }}</textarea>
            <button @click="copyToClipboard('embed', $event)" class="copy-btn-embed">
              <i class="far fa-copy"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="share-modal-footer">
        <button class="share-close-btn" @click="closeModal">CLOSE</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShareModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    scanId: {
      type: [String, Number],
      required: false,
      default: null
    },
    scanType: {
      type: String,
      required: false,
      default: 'quick_scan',
      validator: value => ['quick_scan', 'grid_scan'].includes(value)
    }
  },
  computed: {
    baseUrl() {
      // Get base URL from environment variable or use current origin
      return process.env.VUE_APP_SHARE_BASE_URL || window.location.origin;
    },
    
    gridUrl() {
      if (!this.scanId) return '';
      if (this.scanType === 'grid_scan') {
        return `${this.baseUrl}/recurring-grid/${this.scanId}/share`;
      } else {
        return `${this.baseUrl}/view-grid/${this.scanId}/share`;
      }
    },
    
    reportUrl() {
      if (!this.scanId) return '';
      if (this.scanType === 'grid_scan') {
        return `${this.baseUrl}/recurring-report/${this.scanId}`;
      } else {
        return `${this.baseUrl}/scan-report/${this.scanId}`;
      }
    },
    
    embedCode() {
      if (!this.scanId) return '';
      const embedUrl = this.scanType === 'grid_scan' 
        ? `/recurring-grid/${this.scanId}/embedded`
        : `/view-grid/${this.scanId}/embedded`;
      
      return `<!-- Map Grid Report Embed -->
<div class="map-grid-embed" aria-label="Map grid report">
  <iframe
    src="${this.baseUrl}${embedUrl}"
    title="Map Grid Report — ${this.scanType === 'grid_scan' ? 'Recurring' : 'Quick'} Scan"
    width="100%"
    height="100%"
    style="border:0; border-radius:8px;"
    loading="lazy">
  </iframe>
</div>
<p class="embed-info">
  <a href="${this.gridUrl}" target="_blank">View full report→</a>
</p>`;
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    async copyToClipboard(type, event) {
      let textToCopy = '';
      let inputRef = null;
      
      switch(type) {
        case 'grid':
          textToCopy = this.gridUrl;
          inputRef = this.$refs.gridUrlInput;
          break;
        case 'report':
          textToCopy = this.reportUrl;
          inputRef = this.$refs.reportUrlInput;
          break;
        case 'embed':
          textToCopy = this.embedCode;
          inputRef = this.$refs.embedCodeTextarea;
          break;
      }
      
      try {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(textToCopy);
        } else {
          // Fallback for older browsers
          if (inputRef) {
            inputRef.select();
            inputRef.setSelectionRange(0, 99999); // For mobile devices
            document.execCommand('copy');
            inputRef.blur();
          }
        }
        
        // Show success message
        this.$emit('copied', type);
        
        // Close modal after a short delay
        this.closeModal();
        
        // Change button icon temporarily
        const button = event ? event.currentTarget : null;
        if (button) {
          const icon = button.querySelector('i');
          if (icon) {
            icon.classList.remove('far', 'fa-copy');
            icon.classList.add('fas', 'fa-check');
            button.classList.add('copied');
            
            setTimeout(() => {
              icon.classList.remove('fas', 'fa-check');
              icon.classList.add('far', 'fa-copy');
              button.classList.remove('copied');
            }, 2000);
          }
        }
        
      } catch (err) {
        console.error('Failed to copy:', err);
        // Try one more fallback
        if (inputRef) {
          inputRef.select();
          try {
            document.execCommand('copy');
            this.$emit('copied', type);
          } catch (e) {
            this.$swal({
              icon: 'error',
              title: 'Copy Failed',
              text: 'Failed to copy to clipboard. Please copy manually.',
              timer: 2000,
              showConfirmButton: false
            });
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.share-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.share-modal-content {
  background: white;
  border-radius: 8px;
  width: 680px;
  max-width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  position: relative;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.share-modal-header {
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 20px 50px;
  background: #19233A;
  border-radius: 8px 8px 0 0;
}

.share-modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.share-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  padding: 0;
  z-index: 10;
  font-size: 24px;
  line-height: 1;
}

.share-modal-close span {
  display: block;
  font-size: 24px;
  font-weight: 300;
  margin-top: -2px;
}

.share-modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.share-modal-body {
  padding: 30px 35px;
  background: white;
}

.share-url-section {
  margin-bottom: 20px;
}

.share-url-section:last-child {
  margin-bottom: 0;
}

.share-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.share-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.share-input {
  width: 100%;
  padding: 10px 45px 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 13px;
  color: #374151;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: all 0.2s;
}

.share-input:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2);
}

.copy-btn-inline {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn-inline:hover {
  color: #3b82f6;
}

.copy-btn-inline.copied {
  color: #10b981;
}

.copy-btn-inline i {
  font-size: 14px;
}

.share-embed-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.embed-code-preview {
  width: 100%;
  padding: 10px 36px 10px 10px;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.4;
  color: #374151;
  resize: none;
  height: 140px;
  overflow-y: auto;
}

.embed-code-preview:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2);
}

.embed-code-preview::-webkit-scrollbar {
  width: 6px;
}

.embed-code-preview::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.embed-code-preview::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.embed-code-preview::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.copy-btn-embed {
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.copy-btn-embed:hover {
  color: #3b82f6;
}

.copy-btn-embed.copied {
  color: #10b981;
}

.copy-btn-embed i {
  font-size: 14px;
}

.share-modal-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: end;
  background: #ffffff;
  border-radius: 0 0 8px 8px;
}

.share-close-btn {
  padding: 10px 32px;
  background: white;
  color: #2477F6;
  border: 2px solid #2477F6;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.share-close-btn:hover {
  background: #2477F6;
  color: white;
}

.share-close-btn:active {
  background: #1e5fc6;
  border-color: #1e5fc6;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .share-modal-content {
    width: 95%;
    margin: 20px;
  }
  
  .embed-code-preview {
    font-size: 10px;
    min-height: 100px;
    max-height: 150px;
  }
}
</style>